import m from "mithril";
import _ from "underscore";
import {XmlTagProvider,XmlTagInfo, XmlTagScope} from './xmlTagProvider';

export interface Point {
	x: number,
	y: number
}

export interface TypedClass {
	className: string;
}

export function	request(options:any){
	// default get
	options.method = options.method || 'GET';

	// disable json parsing
	if(options.dataType === "text"){
		options.deserialize = (data: any) => data;
	}
	return m.request(options);
}

export function	getOrCreate<T>(object: any, propertyName: string, constructor: ()=>T ): T {
	if(!object[propertyName]){
		object[propertyName] = constructor();
	}
	return object[propertyName];
}

export function	guid4(){
	// http://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript#answer-2117523
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = Math.random()*16|0, v = c === 'x' ? r : (r&0x3|0x8);
		return v.toString(16);
	});
}

export function	timestamp(){
	return new Date().toISOString(); //TODO: shim for old browsers
}

// TODO: should be removed once xpath for tags is fully used.
export function	traverse(nodes: XmlTagInfo[], handlers: any, scope?: XmlTagScope){
	return traverseInner(nodes, new XmlTagProvider(handlers), scope, []);
}

function traverseInner(nodes: XmlTagInfo[], handlers: any, scope?: XmlTagScope, path: any[] = []): any[] {
	return _.map(nodes, (n:XmlTagInfo) => {
		const currentPath = path.concat(n.tag);
		const handler = handlers.get(currentPath) || function(i:any) { delete i.scope; return i; };
		return handler(_.extend({}, n, {
			children: traverseInner(n.children, handlers, scope, currentPath),
			scope: scope
		}));
	});
}

export class XmlUtils {
	public static childByName(node: XmlTagInfo, name: string) : XmlTagInfo {
		return XmlUtils.childrenByName(node, name)[0];
	}
	public static childrenByName(node: XmlTagInfo, name: string) : XmlTagInfo[] {
		return node.children.filter((n:XmlTagInfo) => n.tag === name);
	}
}

/**
 * For non-convertible value returns undefined value
 * @param any
 * @returns {number} | undefined
 */
export function anyToNumeric(any: any) : number|undefined {
    if (_.isUndefined(any) || _.isNumber(any)) { return any; }
    if (!_.isString(any) || any === '') { return undefined; }
    const v = parseFloat(any.toString().replace(',','.'));
    return _.isNaN(v) ? undefined : v;
}

/**
 * Bootstrap sizes:
 *
 *  <576px	    Extra small
 *  ≥576px	    Small
 *  ≥768px	    Medium
 *  ≥992px	    Large
 *  ≥1200px     Extra large
 */
export function screenSizeTextual(): string {
    let size = window.innerWidth;
    if (size >= 992) {
        return 'large';
    }
    if (size >= 768) {
        return 'medium';
    }
    return 'small';
}

export function arrayIntersect<T>(a: T[], b: T[]): T[] {
    return a.filter((n) => b.indexOf(n) !== -1);
}

export function loadAndRunJs(url: string, onFulfilled?: ()=>any, onReject?: ()=>any): void {
    const newScript = document.createElement("script");
    if (onReject) {
        newScript.onerror = onReject;
    }
    if (onFulfilled) {
        newScript.onload = onFulfilled;
    }
    document.head.appendChild(newScript);
    newScript.src = url;
}

export function loadStyles(href: string, options?: {integrity: string, crossorigin: string}) {
    return new Promise((resolve, reject) => {
        const stylesheet = document.createElement("link");
        stylesheet.setAttribute("rel", "stylesheet");
        Object.entries(options ?? {}).forEach(([attr, value]) => stylesheet.setAttribute(attr, value));

        stylesheet.onload = resolve;
        stylesheet.onerror = reject;
        stylesheet.setAttribute("href", href);

        document.head.appendChild(stylesheet);
    });
}


/**
 * Compare strings in natural way
 *  - a or b is null or undefined => false
 *  - 2 < 10 => true
 *  - "aaa1" < "aAa2" => true
 *  - "doc_9" < "doc_21" => true
 */
export function areStringInNaturalOrder(a?: null|number|string, b?: null|number|string): boolean {
    if (a === null || b === null || a === undefined || b === undefined) {
        return false;
    }
    a = String(a);
    b = String(b);
    return a.localeCompare(b, undefined, {numeric: true, sensitivity: 'base'}) <= 0
}

export function isEmpty(value: any): boolean {
    return value === undefined || value === null || value === '';
}